/** @file Checks the files of a bundle against its manifest: presence, byte counts, hashes. */
import { fnv1a64 } from '@zilo/pose3d-export/hash/fnv1a64';
import { utf8 } from '@zilo/pose3d-export/text/utf8';
import type { BundleError } from '@zilo/pose3d-export/check/bundleError';
import type { BundleManifest } from '@zilo/pose3d-export/types/bundle';

/**
 * One error per `manifest.files` path that is missing from `files` (`BUNDLE_FILE_MISSING`) or whose byte count or
 * hash differs (`BUNDLE_HASH`); the hash is checked with `hash` when given, else with fnv1a64 for `fnv1a64` bundles.
 */
export function checkBundleFiles(manifest: BundleManifest, files: Record<string, Uint8Array | string>, hash?: (bytes: Uint8Array) => string): BundleError[] {
  const errors: BundleError[] = [];
  const hasher = hash ?? (manifest.hashAlgo === 'fnv1a64' ? fnv1a64 : undefined);
  for (const [path, want] of Object.entries(manifest.files)) {
    const content = Object.hasOwn(files, path) ? files[path] : undefined;
    if (content === undefined) {
      errors.push({ code: 'BUNDLE_FILE_MISSING', path: `/files/${path}`, message: `file ${path} is missing` });
      continue;
    }
    const bytes = typeof content === 'string' ? utf8(content) : content;
    if (bytes.length !== want.bytes) {
      errors.push({ code: 'BUNDLE_HASH', path: `/files/${path}`, message: `file ${path} has ${bytes.length} bytes, the manifest says ${want.bytes}` });
    } else if (hasher !== undefined && hasher(bytes) !== want.hash) {
      errors.push({ code: 'BUNDLE_HASH', path: `/files/${path}`, message: `file ${path} does not match its ${manifest.hashAlgo} hash` });
    }
  }
  return errors;
}
